import React from "react";
import { cn } from "@/lib/utils";
import { LosLogo } from "@/components/Logo";
import { TextSection } from "@/components/canvas/TextSection";

/** Price cell straight from the viewmodel – the server decided the wording, so screen, HTML and PDF say the same. */
function Price({ p, testId, card, customerView }) {
  const quiet = customerView && p.kind === "missing";
  const tone = p.kind === "missing" ? (quiet ? "text-muted-foreground/50" : "font-medium text-[hsl(var(--v-assumed))]") : ["included", "etter_regning", "not_offered"].includes(p.kind) || (p.kind === "unit" && !p.in_sum) ? "text-muted-foreground" : "font-medium text-foreground";
  return (
    <>
      <div className={cn("text-[13.5px] tabular-nums", tone, card && "shrink-0 text-right")} data-testid={testId} data-kind={p.kind} title={quiet ? "Ikke priset ennå" : undefined}>{quiet ? "–" : p.text}</div>
      {p.sub ? <div className="text-[11px] text-muted-foreground">{p.sub}</div> : null}
    </>
  );
}

function Line({ ln, customerView, onOpen }) {
  const d = ln.depth;
  return (
    <tr onClick={onOpen ? () => onOpen(ln.ref) : undefined} className={cn("group", d && "text-foreground/85", onOpen && "cursor-pointer transition-colors hover:bg-[hsl(210_20%_97%)]")} title={onOpen ? "Åpne posten i kalkylen" : undefined} data-testid={`tilbud-line-${ln.ref}`}>
      <td className={cn("w-[76px] py-2.5 pr-2 align-top font-mono text-[12px]", d ? "pl-5 text-muted-foreground" : "text-foreground/70")}>{ln.ref}</td>
      <td className={cn("py-2.5 pr-4 align-top", d && "relative pl-4 before:absolute before:bottom-2.5 before:left-0 before:top-2.5 before:w-px before:bg-black/[0.12]")}>
        <div className={cn("text-[13.5px] leading-[1.45]", !d && "font-medium")}>{ln.text}</div>
        {ln.customer_title ? <div className="mt-0.5 text-[11.5px] text-muted-foreground">Kundens post: {ln.customer_title}{ln.wo_no ? ` · WO ${ln.wo_no}` : ""}</div> : null}
      </td>
      <td className="w-[170px] py-2.5 align-top text-right"><Price p={ln.price} testId={`tilbud-price-${ln.ref}`} customerView={customerView} /></td>
    </tr>
  );
}

function LineCard({ ln, customerView, onOpen }) {
  const d = ln.depth;
  return (
    <li onClick={onOpen ? () => onOpen(ln.ref) : undefined} className={cn("px-4 py-3", d && "pl-8", onOpen && "cursor-pointer transition-colors hover:bg-[hsl(210_20%_97%)]")} data-testid={`tilbud-line-${ln.ref}`}>
      <div className="flex items-start justify-between gap-3">
        <span className={cn("font-mono text-[12px]", d ? "text-muted-foreground" : "text-foreground/70")}>{ln.ref}</span>
        <div><Price p={ln.price} testId={`tilbud-price-${ln.ref}`} card customerView={customerView} /></div>
      </div>
      <div className={cn("mt-0.5 text-[13.5px] leading-snug", !d && "font-medium")}>{ln.text}</div>
    </li>
  );
}

function Lines({ rows, wide, testId, footer, customerView, onOpen }) {
  const flat = rows.flatMap((ln) => [ln, ...(ln.children || [])]);
  if (wide) {
    return (
      <table className="w-full table-fixed border-collapse" data-testid={testId}>
        <thead>
          <tr className="border-b-[1.5px] border-[hsl(var(--brand))] text-[11px] font-medium text-muted-foreground">
            <th className="w-[76px] pb-1.5 text-left font-medium">Post</th><th className="pb-1.5 text-left font-medium">Leveranse</th><th className="w-[170px] pb-1.5 text-right font-medium">Pris NOK eks. mva</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-black/[0.07]">{flat.map((ln) => <Line key={ln.id} ln={ln} customerView={customerView} onOpen={onOpen} />)}</tbody>
        {footer ? (
          <tfoot>
            <tr className="border-t-[1.5px] border-[hsl(var(--brand))]">
              <td colSpan={2} className="pt-3 text-[13.5px] font-semibold">{footer.label}</td>
              <td className="pt-3 text-right text-[17px] font-semibold tabular-nums text-[hsl(var(--brand-strong))]" data-testid="tilbud-total">{footer.value}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    );
  }
  return (
    <div data-testid={testId}>
      <ul className="divide-y divide-black/[0.06]">{flat.map((ln) => <LineCard key={ln.id} ln={ln} customerView={customerView} onOpen={onOpen} />)}</ul>
      {footer ? <div className="flex items-center justify-between gap-3 border-t-[1.5px] border-[hsl(var(--brand))] px-4 py-3"><span className="text-[13.5px] font-semibold">{footer.label}</span><span className="text-[17px] font-semibold tabular-nums text-[hsl(var(--brand-strong))]" data-testid="tilbud-total">{footer.value}</span></div> : null}
    </div>
  );
}

function Heading({ children }) {
  return <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--brand-strong))]">{children}</h3>;
}

/** Logo, company and the quote identity – once, at the top of the one document. */
function DocHead({ c, k, vm, status }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-6">
      <div>
        <LosLogo className="text-[20px]" />
        <div className="mt-2 text-[11.5px] leading-[1.5] text-muted-foreground">{c.legal_name || c.name || "Skarpenord AS"}{c.address ? <><br />{c.address}</> : null}</div>
      </div>
      <div className="text-right text-[12.5px] leading-[1.6] text-muted-foreground">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--brand-strong))]">Tilbud</div>
        <div><span className="text-[15px] font-semibold tabular-nums text-foreground">{k.number}</span>{vm.revision ? <span className="tabular-nums text-foreground"> · rev. {vm.revision}</span> : null}</div>
        <div>{vm.date_text}</div>
        <div className={cn("mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10.5px] font-medium", status === "kontrollert" ? "border-[hsl(var(--v-ok))]/40 text-[hsl(var(--v-ok))]" : "border-black/[0.14] text-muted-foreground")} data-testid="tilbud-doc-status">{status === "kontrollert" ? "Kontrollert" : "Utkast"}</div>
      </div>
    </header>
  );
}

/** Name and title of the person responsible for the case – no invented authority, nothing else. */
function Signer({ s, co, testId }) {
  if (!s?.name) return null;
  return (
    <div className="mt-10 text-[13.5px] leading-[1.55]" data-testid={testId}>
      <div className="mb-6">Med vennlig hilsen<br /><span className="font-semibold">{co}</span></div>
      <div className="font-semibold">{s.name}</div>
      {s.title ? <div className="text-foreground/70">{s.title}</div> : null}
      {s.email ? <div className="text-foreground/70">{s.email}</div> : null}
    </div>
  );
}

/**
 * The customer document rendered from a viewmodel – the same whitelist the server uses for HTML and PDF, as one continuous
 * document: head, recipient, date, title, the cover letter as the opening paragraphs, then the offer itself, and one sign-off
 * at the end. `onSaveText(key, op)` makes the text sections editable (Rediger) with the library picker; `customerView` shows
 * exactly what the customer gets. `onOpenLine(ref)` (Rediger) opens a priced line in the calculation; `letterTools`/`letterReview`
 * sit with the opening paragraphs (propose a letter, review a proposal).
 */
export function QuoteView({ vm, wide, onSaveText, texts = {}, library = {}, status, customerView = false, onOpenLine, letterTools = null, letterReview = null }) {
  const c = vm.company || {};
  const k = vm.case || {};
  const t = vm.totals || {};
  const txt = vm.texts || {};
  const co = c.legal_name || c.name || "Skarpenord AS";
  const editable = Boolean(onSaveText);
  const sec = (key, extra = {}) => <TextSection key={key} k={key} label={texts[key]?.label || extra.label || key} value={txt[key]} r={texts[key]} library={library[key] || []} onSave={editable ? (op) => onSaveText(key, op) : undefined} {...extra} />;
  const facts = [["Kunde", k.customer], ["Fartøy", k.vessel], ["Levering", k.period_text], ["Igangkjøring", k.commissioning_days ? `${k.commissioning_days} dager` : null]];
  const letter = Boolean(txt.folgebrev);
  const openLine = !customerView && onOpenLine ? onOpenLine : undefined;
  return (
    <article className="tilbud-sheet px-5 py-8 sm:px-12 sm:py-12" data-testid="tilbud-document" data-revision={vm.revision || "live"} data-letter={letter ? "1" : "0"} data-customer={customerView ? "1" : "0"}>
      <DocHead c={c} k={k} vm={vm} status={status} />

      <div className="mt-10 text-[14px] leading-[1.5]" data-testid="tilbud-letter-addr">
        {k.customer ? <><div className="font-semibold">{k.customer}</div>{k.contact ? <div>v/ {k.contact}</div> : null}</> : editable ? <div className="text-muted-foreground">Mottaker ikke oppgitt</div> : null}
      </div>
      <div className="mt-7 text-[12.5px] text-muted-foreground">Rjukan, {vm.date_text}</div>
      <h1 className="mt-2 scroll-mt-28 font-display text-[24px] font-bold leading-[1.2] tracking-[-0.02em] text-[hsl(var(--navy))] sm:text-[27px]" data-section="hode">Tilbud {k.number}{vm.revision ? ` rev. ${vm.revision}` : ""} – {k.name}</h1>

      {/* The cover letter is simply how the document opens – paragraphs under the title, no separate page or box. */}
      <div className="mt-5" data-testid="tilbud-letter" data-letter={letter ? "1" : "0"}>
        {sec("folgebrev", { heading: false, className: "mt-0", tools: letterTools, emptyLabel: "Skriv følgebrev" })}
        {editable && letterReview ? <div className="mt-4">{letterReview}</div> : null}
      </div>

      <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 border-y border-black/[0.08] py-4 text-[13px] sm:grid-cols-4" data-testid="tilbud-meta">
        {facts.map(([l, v]) => <div key={l} className="min-w-0"><dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{l}</dt><dd className="mt-0.5 truncate font-medium text-foreground/90" title={v || undefined}>{v || "–"}</dd></div>)}
      </dl>

      {sec("omfang")}

      <section className="mt-9 scroll-mt-28" data-section="priser">
        <Heading>Leveranse og priser</Heading>
        <div className="mt-3">
          <Lines rows={vm.lines || []} wide={wide} testId="tilbud-table" customerView={customerView} onOpen={openLine} footer={{ label: <>Sum eks. mva{t.preliminary ? <span className={cn("ml-2 text-[11.5px] font-normal", customerView ? "text-muted-foreground" : "text-[hsl(var(--v-assumed))]")} data-testid="tilbud-preliminary-note">foreløpig – {[t.missing_count ? `${t.missing_count} ${t.missing_count === 1 ? "post" : "poster"} uten pris` : null, t.pending_count ? `${t.pending_count} ${t.pending_count === 1 ? "post" : "poster"} ikke ferdig vurdert` : null].filter(Boolean).join(", ") || "ikke komplett"}</span> : null}</>, value: t.preliminary && !t.price ? "–" : t.price_text }} />
        </div>
        <p className="mt-2.5 text-[11.5px] leading-[1.5] text-muted-foreground">{t.vat_note} Enhetspriser uten antatt mengde, poster etter regning og opsjoner inngår ikke i summen.</p>
      </section>

      {vm.options?.length ? (
        <section className="mt-9 scroll-mt-28" data-testid="tilbud-options" data-section="opsjoner">
          <Heading>Opsjoner (ikke i sum)</Heading>
          <div className="mt-3"><Lines rows={vm.options} wide={wide} testId="tilbud-options-table" customerView={customerView} onOpen={openLine} /></div>
        </section>
      ) : null}

      {vm.assumptions?.kunde?.length || vm.assumptions?.los?.length ? (
        <section className="mt-9 grid scroll-mt-28 gap-8 sm:grid-cols-2" data-testid="tilbud-assumptions" data-section="forutsetninger-poster">
          {vm.assumptions.kunde.length ? <div><Heading>Kunden sørger for</Heading><ul className="mt-2.5 space-y-2">{vm.assumptions.kunde.map((a, i) => <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.5]"><span className="w-[52px] shrink-0 font-mono text-[11px] text-muted-foreground">{a.ref}</span>{a.text}</li>)}</ul></div> : null}
          {vm.assumptions.los.length ? <div><Heading>Forutsetninger per post</Heading><ul className="mt-2.5 space-y-2">{vm.assumptions.los.map((a, i) => <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.5]"><span className="w-[52px] shrink-0 font-mono text-[11px] text-muted-foreground">{a.ref}</span>{a.text}</li>)}</ul></div> : null}
        </section>
      ) : null}

      {vm.not_offered?.length ? <p className="mt-8 text-[12px] text-muted-foreground" data-testid="tilbud-skipped">Ikke tilbudt: {vm.not_offered.join(", ")}</p> : null}

      {sec("forbehold")}
      {sec("forutsetninger")}
      {sec("vilkar")}
      {sec("gyldighet")}
      {sec("avslutning", { heading: false })}
      {vm.signoff?.end ? <Signer s={vm.signer} co={co} testId="tilbud-signer" /> : null}

      {vm.footnote ? <p className="mt-8 rounded-md border border-dashed border-black/[0.18] px-3 py-2 text-[11.5px] text-foreground/70" data-testid="tilbud-footnote">{vm.footnote}</p> : null}

      <footer className="mt-12 flex flex-wrap items-end justify-between gap-6 border-t border-black/[0.08] pt-5 text-[11.5px] text-muted-foreground">
        <div>
          <div className="font-medium text-foreground/80">{co}</div>
          <div>{[c.address, c.phone, c.email, c.website].filter(Boolean).join(" · ")}</div>
          {c.org_no ? <div>Org.nr. {c.org_no}</div> : null}
        </div>
        <div className="text-right">Tilbud {k.number}{vm.revision ? ` · rev. ${vm.revision}` : ""} · {vm.date_text}</div>
      </footer>
    </article>
  );
}
